import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback } from "react";

import { SingleSelectInput, toast } from "@performyard/py-components";
import type { Option } from "@performyard/py-components/src/types/Option";

import { createFetchEmployeesForPicker } from "../../../components/Inputs/SelectEmployee";
import { AssigneeLabel } from "./components/AssigneeLabel";

import { ASSIGNEE_WILDCARDS, type AssigneeValue, type AssigneeWildcard } from "./launch-form";

export const AssigneeSelect = ({
	value,
	onChange,
	wildcards = ASSIGNEE_WILDCARDS,
	errored,
	placeholder = "Select an assignee...",
	ariaLabel = "Assignee",
	id,
	"data-testid": dataTestId,
}: {
	value: AssigneeValue | null;
	onChange: (value: AssigneeValue | null) => void;
	wildcards?: readonly AssigneeWildcard[];
	errored?: boolean;
	placeholder?: string;
	ariaLabel?: string;
	id?: string;
	"data-testid"?: string;
}) => {
	const queryClient = useQueryClient();

	const fetchOptions = useCallback(
		async (searchTerm: string): Promise<Option<AssigneeValue>[]> => {
			const term = searchTerm.trim().toLowerCase();
			const wildcardOptions = wildcards
				.filter((wildcard) => !term || wildcard.name.toLowerCase().includes(term))
				.map((wildcard) => ({ label: wildcard.name, value: { id: wildcard.id, name: wildcard.name } }));

			let employeeOptions: Option<AssigneeValue>[] = [];
			try {
				const employees = await createFetchEmployeesForPicker(queryClient)(searchTerm);
				employeeOptions = employees.map((employee) => ({
					label: employee.name,
					value: { id: employee.id, name: employee.name },
				}));
			} catch (error) {
				Sentry.captureException(error);
				console.error("Error fetching employees:", error);
				toast.error("There was an error searching employees.");
			}

			return [...wildcardOptions, ...employeeOptions];
		},
		[queryClient, wildcards]
	);

	const renderAssignee = useCallback((assignee: AssigneeValue) => <AssigneeLabel assignee={assignee} />, []);

	return (
		<SingleSelectInput<AssigneeValue>
			fetchOptions={fetchOptions}
			value={value ?? undefined}
			onChange={onChange}
			selectionVariant="combobox"
			placeholder={placeholder}
			errored={errored}
			id={id}
			data-testid={dataTestId}
			renderValue={renderAssignee}
			renderOption={(option) => renderAssignee(option.value)}
			ariaLabel={ariaLabel}
		/>
	);
};
